/* eslint-disable camelcase */

import { observer } from 'mobx-react-lite';
import { GridRowParams } from '@material-ui/data-grid';
import { Box, Button, ListItem, Typography } from '@material-ui/core';
import VMDataGrid from '../../components/main/VMDataGrid';
import { Api } from '../../api';
import { useProvider } from '../../state/providerStore';
import { gridColumn } from '../../utils/gridUtils';
import TextLink from '../../components/other/TextLink';
import {
  BILLING_TABS,
  billingTabRoute,
  companyDetailsParameters,
  userDetailsTabParameters,
} from '../../utils/routing';
import { formatToSloDate } from '../Sessions/SessionsPage';
import ActionsMenu from '../../components/other/ActionsMenu';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { ColumnStatusBadge } from 'components/other/CellBadges';
import {
  blueColor,
  greenTextColor,
  lightGreenColor,
  lightRedColor,
} from 'components/main/react/App';
import { formatStringValue } from 'utils/utils';
import { ReactElement } from 'react';
import PersonIcon from '@material-ui/icons/Person';
import BusinessIcon from '@material-ui/icons/Business';

const onClickShowInvoiceDetails = (invoice) => {
  drawerStore.openDrawer({
    label: 'Invoice details',
    inputs: [{ name: 'details', type: FormInputTypes.jsonView, value: invoice }],
    clickCallback: undefined,
    buttonLabel: 'Close',
  });

  drawerStore.setValues(invoice);
};

const cartInputs = [
  { name: 'id', type: FormInputTypes.disabledTextField },
  { name: 'reference', type: FormInputTypes.disabledTextField },
  { name: 'status', type: FormInputTypes.disabledTextField },
  { name: 'createdAt', type: FormInputTypes.disabledTextField },
  { name: 'billingToken', type: FormInputTypes.pugCart },
];

export const onClickShowCart = async (cartId: string) => {
  drawerStore.openDrawer({
    label: 'Cart details',
    inputs: [{ name: 'loader', type: FormInputTypes.loader }],
    clickCallback: undefined,
    buttonLabel: 'Close',
  });

  Api.billing.getCart(cartId).then((data) => {
    const cartData = data.data.data;
    drawerStore.setValues(cartData);
    drawerStore.setInputs(cartInputs);
  });
};

const openMarkPaidDrawer = (invoicesProvider: any) => {
  drawerStore.openDrawer({
    label: 'Mark invoice as paid',
    inputs: [{ name: 'invoiceId', type: FormInputTypes.textFieldWithPrefix, prefix: 'SI12 ' }],
    clickCallback: () => {
      Api.billing.markInvoicePaidByReference(drawerStore.values.invoiceId_with_prefix).then(() => {
        invoicesProvider.reloadResource();
      });
    },
    buttonLabel: 'Mark paid',
  });
};

const onClickMarkPaid = async (invoiceId: string, invoicesProvider: any) => {
  Api.billing.markInvoicePaid(invoiceId).then(() => {
    invoicesProvider.reloadResource();
  });
};

const invoiceActions = (params: GridRowParams, invoicesProvider: any) => {
  const invoice = params.row;
  const receipt = params.row?.purchase?.receipt;
  const cartId = params.row?.cartId;
  const status = params.row?.status;

  return (
    <ActionsMenu>
      <ListItem button onClick={() => onClickShowInvoiceDetails(invoice)}>
        <Typography style={{ width: '100%' }}>Details</Typography>
      </ListItem>
      {receipt && (
        <ListItem button onClick={() => window.open(receipt, '_blank')}>
          <Typography style={{ width: '100%' }}>Receipt</Typography>
        </ListItem>
      )}
      {cartId && (
        <ListItem button onClick={() => onClickShowCart(cartId)}>
          <Typography style={{ width: '100%' }}>Cart</Typography>
        </ListItem>
      )}
      {status !== 'paid' && (
        <ListItem button onClick={() => onClickMarkPaid(invoice?.id, invoicesProvider)}>
          <Typography style={{ width: '100%' }}>Mark paid</Typography>
        </ListItem>
      )}
    </ActionsMenu>
  );
};

const getInvoicesColumns = (invoicesProvider) => [
  gridColumn('user', {
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRowParams) => {
      const { row } = params;
      let to = '';
      let type: ReactElement<any, any>;
      if (row.userId) {
        to = userDetailsTabParameters(row.userId);
        type = <PersonIcon fontSize="small" color="primary" />;
      } else if (row.accountId) {
        to = companyDetailsParameters(row.accountId);
        type = <BusinessIcon fontSize="small" color="primary" />;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gridColumnGap: '0.5rem' }}>
          {type}
          <TextLink label={row.customer.name || 'N/A'} to={to} />
        </Box>
      );
    },
  }),
  gridColumn('settlementMode', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      if (!params.row.settlementMode) {
        return '';
      }
      return formatStringValue(params.row.settlementMode);
    },
  }),
  gridColumn('amount', {
    flex: 1,
    minWidth: 100,
    renderCell: (params: GridRowParams) => (
      <Box sx={{ color: greenTextColor }}>{params.row.amount?.formatted}</Box>
    ),
  }),
  gridColumn('reference', {
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRowParams) => <div>{params.row.purchase?.processorId}</div>,
  }),
  gridColumn('contractNo', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => <div>{params.row.contractNo || 'No contract'}</div>,
  }),
  gridColumn('periodFrom', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const date = new Date(params.row.periodFrom);

      if (!date) {
        return <div />;
      }

      const sloDate = formatToSloDate(date);

      return <div>{sloDate}</div>;
    },
  }),
  gridColumn('periodTo', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const date = new Date(params.row.periodTo);

      if (!date) {
        return <div />;
      }

      const sloDate = formatToSloDate(date);

      return <div>{sloDate}</div>;
    },
  }),
  gridColumn('status', {
    flex: 1,
    minWidth: 160,
    renderCell: (params: GridRowParams) => {
      let color = blueColor;
      if (params.row.status === 'paid') {
        color = lightGreenColor;
      } else if (params.row.status === 'overdue') {
        color = lightRedColor;
      }

      return <ColumnStatusBadge label={params.row.status.toUpperCase()} color={color} />;
    },
  }),
  gridColumn(' ', {
    width: 60,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => invoiceActions(params, invoicesProvider),
  }),
];

export const InvoicesPage = observer(() => {
  const invoicesProvider = useProvider(Api.billing.getInvoices);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Button variant="text" color="primary" onClick={() => openMarkPaidDrawer(invoicesProvider)}>
          Mark invoice as paid
        </Button>
      </div>
      <VMDataGrid
        label="Invoices"
        rows={invoicesProvider.data}
        columns={getInvoicesColumns(invoicesProvider)}
      />
    </div>
  );
});
